import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrag } from '@use-gesture/react';
import { useSpring, animated } from '@react-spring/web';
import { Trash2, Link2, Globe, Lock, Check, Loader2, Eye, EyeOff, Power, PowerOff, ShieldCheck, ShieldOff, Pin, Pencil, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API, BACKEND_URL as BASE_URL } from '@/utils/apiConfig';

export default function SwipeableConversationItem({ 
  conversation, 
  isActive, 
  onSelect, 
  onDelete,
  onMoveToVault,
  onMoveFromVault,
  onTogglePin,
  onRename,
  isRenaming = false,
  renameDraft = '',
  setRenameDraft,
  onCommitRename,
  onCancelRename,
  isPrivate = false,
  isGuest = false,
  onGuestBlock,
}) {
  const { t } = useTranslation();
  const [{ x }, api] = useSpring(() => ({ x: 0 }));
  const deleteTriggerRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);
  const [toggling, setToggling] = useState(false);
  const shareRef = useRef(null);

  // Close share popover on outside click
  useEffect(() => {
    if (!shareOpen) return;
    const handler = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [shareOpen]);

  // Fetch share status when popover opens
  useEffect(() => {
    if (!shareOpen) { setShowPasswordInput(false); setPassword(''); return; }
    axios.get(`${API}/deeplinks/conversation/${conversation.conversation_id}/status`, { withCredentials: true })
      .then(res => setShareStatus(res.data))
      .catch(() => setShareStatus(null));
  }, [shareOpen, conversation.conversation_id]);

  const bind = useDrag(({ active, movement: [mx], cancel }) => {
    const DELETE_THRESHOLD = -75;
    const VAULT_THRESHOLD = 75;

    if (mx > 0 && !onMoveToVault && !onMoveFromVault) {
      // No vault action available — block right swipe
      if (active) api.start({ x: 0, immediate: true });
      return;
    }

    if (!active) {
      if (mx < DELETE_THRESHOLD) {
        // Swipe left → delete
        if (deleteTriggerRef.current) deleteTriggerRef.current.click();
      } else if (mx > VAULT_THRESHOLD && (onMoveToVault || onMoveFromVault)) {
        // Swipe right → move to/from vault
        if (isPrivate && onMoveFromVault) onMoveFromVault(conversation.conversation_id);
        else if (onMoveToVault) onMoveToVault(conversation.conversation_id);
      }
      api.start({ x: 0 });
    } else {
      api.start({ x: mx, immediate: true });
    }
  }, { 
    axis: 'x',
    filterTaps: true,
    from: () => [x.get(), 0] 
  });

  const handleCopyLink = async (isPublic, pwd) => {
    setSharing(true);
    try {
      const payload = {
        conversation_id: conversation.conversation_id,
        is_public: isPublic,
      };
      if (!isPublic && pwd) payload.password = pwd;
      const { data } = await axios.post(`${API}/deeplinks/conversation`, payload, { withCredentials: true });

      const url = `${BASE_URL}/api/og${data.url}`;

      // Try clipboard copy with fallback
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        try {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.cssText = 'position:fixed;opacity:0;left:-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          copied = true;
        } catch { /* fallback also failed */ }
      }

      setCopied(true);
      const label = isPublic ? 'Public link' : (pwd ? 'Password-protected link' : 'Private link');
      toast.success(copied ? `${label} copied!` : `${label} created!`);
      setPassword('');
      setShowPasswordInput(false);
      setTimeout(() => { setCopied(false); setShareOpen(false); }, 1500);
    } catch {
      toast.error(t('generated.swipeableconversationitem_could_not_create_a4d8'));
    }
    setSharing(false);
  };

  const handleToggleSharing = async () => {
    setToggling(true);
    try {
      const { data } = await axios.put(`${API}/deeplinks/conversation/${conversation.conversation_id}/toggle`, {}, { withCredentials: true });
      setShareStatus(prev => prev ? { ...prev, is_active: data.is_active } : null);
      toast.success(data.is_active ? 'Sharing enabled' : 'Sharing disabled — link no longer works');
    } catch {
      toast.error(t('generated.swipeableconversationitem_could_not_update_32c6'));
    }
    setToggling(false);
  };

  return (
    <div className="relative mb-1 rounded-lg overflow-visible touch-pan-y group">
      {/* Background: Vault icon on left (swipe right), Trash icon on right (swipe left) */}
      <div className="absolute inset-0 flex items-center justify-between rounded-lg">
        <div className="flex items-center gap-2 pl-4 text-indigo-400">
          {(onMoveToVault || onMoveFromVault) && (
            <>
              {isPrivate ? <ShieldOff className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 pr-4 text-destructive">
          <Trash2 className="h-5 w-5" />
        </div>
      </div>
      <animated.div
        {...bind()}
        style={{ x }}
        className={`group relative flex items-center justify-between p-3 pr-4 gap-2 rounded-lg cursor-pointer transition-colors bg-card border-b border-border/50 ${
          isActive
            ? 'bg-[var(--theme-accentLight)] border-transparent'
            : 'hover:bg-[var(--theme-hover)]'
        }`}
        onClick={() => onSelect(conversation.conversation_id)}
      >
        {isRenaming ? (
          // ─ Inline rename input. Enter saves, Escape cancels, blur saves.
          (<input
            type="text"
            value={renameDraft}
            autoFocus
            onChange={(e) => setRenameDraft && setRenameDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { e.preventDefault(); onCommitRename && onCommitRename(); }
              else if (e.key === 'Escape') { e.preventDefault(); onCancelRename && onCancelRename(); }
            }}
            onBlur={() => onCommitRename && onCommitRename()}
            className="text-sm theme-text flex-1 font-medium min-w-0 pr-1 rounded px-1.5 py-0.5 outline-none"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--theme-accent, #6366f1)',
            }}
            maxLength={200}
            data-testid={`rename-input-${conversation.conversation_id}`}
          />)
        ) : (
          // Double-click the title to rename — works on desktop AND mobile
          // (touch devices trigger a double-tap). Keeps the row visually
          // uncluttered by making the affordance implicit.
          (<span
            className="text-sm theme-text flex-1 font-medium min-w-0 pr-1 select-none line-clamp-2 break-words"
            onDoubleClick={(e) => {
              if (onRename && !isPrivate) {
                e.stopPropagation();
                onRename(conversation);
              }
            }}
            title={onRename && !isPrivate ? 'Double-click to rename' : undefined}
          >
            {conversation.title}
          </span>)
        )}

        {/* Rename pencil — hidden until row hover (desktop) or when the
            row is active (mobile). Sits inside the row's right padding so
            it never overlaps the sidebar scrollbar. */}
        {onRename && !isRenaming && !isPrivate && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-6 w-6 shrink-0 rounded-md transition-opacity duration-150 hover:bg-[var(--theme-accent)]/15 hover:text-[var(--theme-accent)] theme-text-secondary/70 ${
              isActive
                ? 'opacity-70'
                : 'opacity-0 group-hover:opacity-70 focus:opacity-70'
            }`}
            onClick={(e) => { e.stopPropagation(); onRename(conversation); }}
            data-testid={`rename-conv-btn-${conversation.conversation_id}`}
            title={t('generated.swipeableconversationitem_rename_conversation_e476')}
            aria-label={t('generated.swipeableconversationitem_rename_conversation_e476')}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        
        {/* Pin button */}
        {onTogglePin && !isPrivate && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 shrink-0 transition-all ${
              conversation.is_pinned
                ? 'text-[var(--theme-accent)] bg-[var(--theme-accent)]/10'
                : 'hover:bg-[var(--theme-accent)]/10 hover:text-[var(--theme-accent)] theme-text-secondary/60'
            }`}
            onClick={(e) => { e.stopPropagation(); onTogglePin(conversation.conversation_id, conversation.is_pinned); }}
            data-testid={`pin-conv-btn-${conversation.conversation_id}`}
            title={conversation.is_pinned ? 'Unpin conversation' : 'Pin conversation'}
          >
            <Pin className="h-3.5 w-3.5" style={conversation.is_pinned ? { fill: 'currentColor' } : undefined} />
          </Button>
        )}

        {/* Share button */}
        <div ref={shareRef} className="relative shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:bg-[var(--theme-accent)]/10 hover:text-[var(--theme-accent)] transition-all theme-text-secondary/60"
            onClick={(e) => { e.stopPropagation(); if (isGuest) { onGuestBlock?.('share conversations'); } else { setShareOpen(!shareOpen); } }}
            data-testid={`share-conv-btn-${conversation.conversation_id}`}
            style={isGuest ? { opacity: 0.4 } : undefined}
          >
            <Link2 className="h-3.5 w-3.5" />
          </Button>
          {shareOpen && (
            <div
              className="absolute right-0 top-full mt-1 rounded-xl theme-surface border theme-border shadow-xl z-[80] animate-in fade-in slide-in-from-top-2 duration-150"
              style={{ width: showPasswordInput ? '15rem' : '12rem' }}
              data-testid="share-link-popover"
              onClick={(e) => e.stopPropagation()}
            >
              {sharing ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin theme-text-secondary" />
                </div>
              ) : copied ? (
                <div className="flex items-center justify-center gap-2 py-3 text-green-500">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">{t('generated.swipeableconversationitem_copied_aa66')}</span>
                </div>
              ) : (
                <>
                  {/* Public link */}
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors rounded-t-xl"
                    onClick={() => handleCopyLink(true)}
                    data-testid="share-public-btn"
                  >
                    <Globe className="h-4 w-4 text-green-500 shrink-0" />
                    <div className="text-left">
                      <p className="font-medium text-xs">{t('generated.swipeableconversationitem_public_link_3766')}</p>
                      <p className="text-[10px] theme-text-secondary">{t('generated.swipeableconversationitem_anyone_can_view_3290')}</p>
                    </div>
                  </button>

                  {/* Private link with password */}
                  {!showPasswordInput ? (
                    <button
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                      onClick={() => setShowPasswordInput(true)}
                      data-testid="share-private-btn"
                    >
                      <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                      <div className="text-left">
                        <p className="font-medium text-xs">{t('generated.swipeableconversationitem_private_link_ae2a')}</p>
                        <p className="text-[10px] theme-text-secondary">{t('generated.swipeableconversationitem_password_protected_5d33')}</p>
                      </div>
                    </button>
                  ) : (
                    <div className="px-3 py-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        <span className="text-xs font-medium theme-text">{t('generated.swipeableconversationitem_set_a_password_8ab1')}</span>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t('generated.swipeableconversationitem_enter_password_0924')}
                          autoComplete="new-password"
                          name="share-pwd"
                          className="w-full text-xs px-2.5 py-1.5 pr-7 rounded-lg theme-text theme-surface border theme-border focus:outline-none focus:ring-1 focus:ring-[var(--theme-accent)]"
                          style={{ background: 'var(--glass-bg)' }}
                          onKeyDown={(e) => { if (e.key === 'Enter' && password.trim()) handleCopyLink(false, password.trim()); }}
                          autoFocus
                          data-testid="share-password-input"
                        />
                        <button
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 theme-text-secondary hover:theme-text"
                          onClick={() => setShowPassword(!showPassword)}
                          type="button"
                        >
                          {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => { setShowPasswordInput(false); setPassword(''); }}
                          className="flex-1 text-[10px] py-1 rounded-md theme-text-secondary hover:bg-[var(--theme-hover)] transition"
                        >
                          {t('generated.swipeableconversationitem_cancel_c28b')}
                        </button>
                        <button
                          onClick={() => password.trim() && handleCopyLink(false, password.trim())}
                          disabled={!password.trim()}
                          className="flex-1 text-[10px] py-1 rounded-md font-medium text-white transition disabled:opacity-40"
                          style={{ background: 'var(--theme-accent)' }}
                          data-testid="share-password-confirm-btn"
                        >
                          {t('generated.swipeableconversationitem_copy_link_85f8')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Disable/Enable sharing toggle — only if already shared */}
                  {shareStatus?.shared && (
                    <>
                      <div className="mx-3 my-0.5 h-px" style={{ background: 'var(--glass-border)' }} />
                      <button
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors rounded-b-xl"
                        onClick={handleToggleSharing}
                        disabled={toggling}
                        data-testid="share-toggle-btn"
                      >
                        {toggling ? (
                          <Loader2 className="h-4 w-4 animate-spin theme-text-secondary shrink-0" />
                        ) : shareStatus.is_active ? (
                          <PowerOff className="h-4 w-4 text-red-400 shrink-0" />
                        ) : (
                          <Power className="h-4 w-4 text-green-500 shrink-0" />
                        )}
                        <div className="text-left">
                          <p className="font-medium text-xs">
                            {shareStatus.is_active ? 'Disable sharing' : 'Enable sharing'}
                          </p>
                          <p className="text-[10px] theme-text-secondary">
                            {shareStatus.is_active ? 'Link will stop working' : 'Reactivate the link'}
                          </p>
                        </div>
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Vault move button */}
        {(onMoveToVault || onMoveFromVault || isGuest) && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 shrink-0 transition-all ${
              isPrivate 
                ? 'hover:bg-indigo-500/10 hover:text-indigo-400 theme-text-secondary/60'
                : 'hover:bg-indigo-500/10 hover:text-indigo-400 theme-text-secondary/60'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (isGuest) { onGuestBlock?.('the private vault'); return; }
              if (isPrivate && onMoveFromVault) onMoveFromVault(conversation.conversation_id);
              else if (onMoveToVault) onMoveToVault(conversation.conversation_id);
            }}
            data-testid={`vault-move-btn-${conversation.conversation_id}`}
            style={isGuest ? { opacity: 0.4 } : undefined}
            title={isPrivate ? 'Move to regular' : 'Move to private'}
          >
            {isPrivate ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          </Button>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              ref={deleteTriggerRef}
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 hover:bg-destructive/10 hover:text-destructive transition-all"
              onClick={(e) => e.stopPropagation()}
              data-testid={`delete-conv-btn-${conversation.conversation_id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('chat.delete_conv_title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('chat.delete_conv_desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('chat.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => {
                e.stopPropagation();
                onDelete(conversation.conversation_id);
              }}>
                {t('chat.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </animated.div>
    </div>
  );
}
